import { z } from "zod";

import { type RefreshInterval, toRefreshInterval } from "~/components/refresh-interval-selector/utils";
import type { EnforcedKeys } from "~/tools/enforced-keys";
import { filter } from "~/tools/filter";
import { fromEntries } from "~/tools/from-entries";
import { fromJSON } from "~/tools/from-json";
import { ImmutableMap } from "~/tools/immutable-map";
import { isArrayEqual } from "~/tools/is-array-equal";
import { isPresent } from "~/tools/is-present";
import { map } from "~/tools/map";
import type { PickReadonly } from "~/tools/pick-readonly";
import { pipe } from "~/tools/pipe";
import { piped } from "~/tools/piped";
import { prop } from "~/tools/prop";
import { toArray } from "~/tools/to-array";
import { toJSON } from "~/tools/to-json";
import { uniqueBy } from "~/tools/unique-by";

import type { TimeRange } from "../../app/state/view/timeline/TimeRange";
import { OBSERVABILITY_PROMQL_VARIABLES } from "../../routes/dashboards/built-in-variables";
import { TimeRangeParam } from "../../routes/timeline/TimelineQueryUtils";
import { Api, type PersesDashboard } from "../openapi";

import { DashboardLayoutSpec } from "./_dashboard-layout-spec";
import { DashboardPanelSpec } from "./_dashboard-panel-spec";
import { DashboardPanelsSpec } from "./_dashboard-panels-spec";
import { DashboardVariable } from "./_dashboard-variable";
import { DashboardWidgetSpec, type DashboardWidgetSpecInitConfig } from "./_dashboard-widget-spec";

const OBSERVABILITY_PROMQL_VARIABLES_SET = new Set<string>(OBSERVABILITY_PROMQL_VARIABLES);

const metadataSchema = z
	.string()
	.catch("")
	.transform((value) => {
		const [, result] = fromJSON(value);

		return result?.unsafe;
	})
	.pipe(
		z
			.object({
				saveVariables: z.boolean().catch(false),
			})
			.catch({
				saveVariables: false,
			}),
	);

export type DashboardSpecConfig = PickReadonly<Omit<DashboardSpec, "update">>;

export type DashboardSpecInitConfig = EnforcedKeys<DashboardSpecConfig, "panels">;

export class DashboardSpec {
	public static schema = z
		.object({
			spec: z.object({
				layouts: z.tuple([
					z
						.object({
							spec: z.object({
								items: z.array(DashboardLayoutSpec.schema.optional().catch(undefined)).transform(filter(isPresent)),
							}),
						})
						.catch({
							spec: {
								items: [],
							},
						}),
				]),

				panels: z
					.record(
						z.string(),
						z
							.object({
								spec: DashboardWidgetSpec.schema,
							})
							.optional()
							.catch(undefined),
					)
					.catch({}),

				variables: z
					.array(DashboardVariable.schema.optional().catch(undefined))
					.transform(
						piped(
							filter(isPresent),
							filter(({ name }) => !OBSERVABILITY_PROMQL_VARIABLES_SET.has(name)),
							uniqueBy(prop("name")),
						),
					)
					.catch([]),

				duration: z
					.string()
					.catch("")
					.transform((duration) => TimeRangeParam.decode(duration) ?? null),

				refreshInterval: z
					.string()
					.catch("")
					.transform((refreshInterval) => toRefreshInterval(refreshInterval) ?? null),
			}),

			metadata: z.object({
				project: metadataSchema,
			}),
		})
		.transform(
			({
				metadata,
				spec: {
					layouts: [layout],
					panels,
					variables,
					duration,
					refreshInterval,
				},
			}) => {
				const panelsList = pipe(
					layout.spec.items,
					map(([panelId, panelLayout]) => {
						const panelWidget = panels[panelId];

						if (panelWidget == null) {
							return null;
						}

						return new DashboardPanelSpec(panelId, panelLayout, panelWidget.spec);
					}),
					filter(isPresent),
				);

				const panelsMap = ImmutableMap.fromList(prop("id"), panelsList, {
					isValueEqual: (left, right) => left.isEqual(right),
				});

				return new DashboardSpec(
					new DashboardPanelsSpec(panelsMap),
					variables,
					duration,
					refreshInterval,
					metadata.project.saveVariables,
				);
			},
		);

	public static fromPerses(dashboard: PersesDashboard): DashboardSpec {
		const result = DashboardSpec.schema.safeParse(dashboard);

		if (result.success) {
			return result.data;
		}

		return DashboardSpec.of({
			panels: new DashboardPanelsSpec(
				ImmutableMap.empty({
					isValueEqual: (left, right) => left.isEqual(right),
				}),
			),
		});
	}

	public static of({
		panels,
		variables = [],
		timeRange = null,
		refreshInterval = null,
		saveVariables = false,
	}: DashboardSpecInitConfig): DashboardSpec {
		return new DashboardSpec(panels, variables, timeRange, refreshInterval, saveVariables);
	}

	private constructor(
		public readonly panels: DashboardPanelsSpec,
		public readonly variables: ReadonlyArray<DashboardVariable>,
		public readonly timeRange: null | TimeRange,
		public readonly refreshInterval: null | RefreshInterval,
		public readonly saveVariables: boolean,
	) {}

	public isEqual(another: DashboardSpec): boolean {
		if (this === another) {
			return true;
		}

		return (
			another.timeRange?.toString() === this.timeRange?.toString() &&
			this.refreshInterval === another.refreshInterval &&
			this.saveVariables === another.saveVariables &&
			isArrayEqual(this.variables, another.variables, (left, right) => left.isEqual(right)) &&
			this.panels.isEqual(another.panels)
		);
	}

	public update({
		panels = this.panels,
		variables = this.variables,
		timeRange = this.timeRange,
		refreshInterval = this.refreshInterval,
		saveVariables = this.saveVariables,
	}: Partial<DashboardSpecConfig>): DashboardSpec {
		const next = new DashboardSpec(panels, variables, timeRange, refreshInterval, saveVariables);

		return this.isEqual(next) ? this : next;
	}

	public addWidgets(widgets: ReadonlyArray<DashboardWidgetSpecInitConfig>): DashboardSpec {
		if (widgets.length === 0) {
			return this;
		}

		let panels = this.panels;

		for (const widget of widgets) {
			const panel = DashboardPanelSpec.fromWidget(widget);

			panels = panels.addPanel(panel);
		}

		return new DashboardSpec(panels, this.variables, this.timeRange, this.refreshInterval, this.saveVariables);
	}

	public toPerses(): PersesDashboard {
		const panels = toArray(this.panels.values());

		return {
			metadata: {
				project: toJSON({
					saveVariables: this.saveVariables,
				}),
			},
			spec: {
				panels: fromEntries(panels.map(({ id, widget }) => [id, widget.toPerses()])),
				layouts: [
					{
						kind: Api.PersesLayoutKind.Grid,
						spec: {
							items: panels.map(({ id, layout }) => layout.toPerses(id)),
						},
					},
				],
				variables: this.variables.map((variable) => variable.toPerses(this.saveVariables)),
				duration: TimeRangeParam.encode(this.timeRange ?? undefined)?.toString(),
				refreshInterval: this.refreshInterval ?? undefined,
			},
		};
	}
}
